import { Column, Row } from 'src/shared/components/Semantic/all'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import type { Quote_ } from 'src/api/db/apiQuote'
import type { BillingMode } from '../types'
import {
  formatDocumentDate,
  formatPartyAddress,
  getBillingDocumentData,
} from '../utils'
import { DocumentField } from './DocumentField'

const PartyBlock = ({
  title,
  party,
  showFTax,
}: {
  title: string
  party: ReturnType<typeof getBillingDocumentData>['seller']
  showFTax?: boolean
}) => {
  const { t } = useLocalization()

  return (
    <Column className="gap-1 min-w-[12rem] max-w-xs">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {title}
      </span>
      <span className="text-sm font-medium text-foreground">
        {party.name || '—'}
      </span>
      {formatPartyAddress(party)
        .split('\n')
        .filter((line) => line !== '—')
        .map((line, index) => (
          <span
            key={`${index}-${line}`}
            className="text-xs text-muted-foreground"
          >
            {line}
          </span>
        ))}
      {party.orgNumber && (
        <span className="text-xs text-muted-foreground">
          {t('Org. no', 'Org.nr')} {party.orgNumber}
        </span>
      )}
      {party.vatNumber && (
        <span className="text-xs text-muted-foreground">
          {t('VAT no', 'Momsnr')} {party.vatNumber}
        </span>
      )}
      {party.email && (
        <span className="text-xs text-muted-foreground">{party.email}</span>
      )}
      {showFTax && (
        <span className="text-xs text-muted-foreground">
          {t('Approved for F-tax', 'Godkänd för F-skatt')}
        </span>
      )}
    </Column>
  )
}

export const DocumentHeader = ({
  mode,
  quote,
  isAdmin = false,
}: {
  mode: BillingMode
  quote: Quote_
  isAdmin?: boolean
}) => {
  const { t } = useLocalization()
  const isInvoice = mode === 'invoice'
  const invoice = getBillingDocumentData(quote)
  const paymentTermsLabel = t(
    `${invoice.paymentTermsDays} days net`,
    `${invoice.paymentTermsDays} dagar netto`
  )

  const documentNumber = isInvoice
    ? invoice.invoiceNumber
    : `Q-${quote.public_id ?? quote.id}`

  if (!isInvoice) {
    return (
      <Column className="gap-6">
        <Row className="justify-between items-start gap-4">
          <Column className="gap-1">
            <h2>{t('Quote', 'Offert')}</h2>
            <span className="text-sm text-muted-foreground">
              {documentNumber}
            </span>
          </Column>
          {isAdmin ? (
            <Column className="items-end gap-0.5 text-right">
              <span className="text-sm font-medium text-foreground">
                {invoice.seller.name}
              </span>
              <span className="text-xs text-muted-foreground">
                {t('Org. no', 'Org.nr')} {invoice.seller.orgNumber}
              </span>
            </Column>
          ) : null}
        </Row>

        {isAdmin ? (
          <>
            <Row className="justify-between items-start gap-8 flex-wrap">
              <PartyBlock
                title={t('Seller', 'Säljare')}
                party={invoice.seller}
                showFTax={invoice.fTaxApproved}
              />
              <PartyBlock title={t('Buyer', 'Köpare')} party={invoice.buyer} />
            </Row>

            <Row className="flex-wrap gap-x-12 gap-y-4">
              <DocumentField
                label={t('Issue date', 'Utfärdad')}
                value={formatDocumentDate(quote.created_at)}
              />
              <DocumentField
                label={t('Payment terms', 'Betalningsvillkor')}
                value={paymentTermsLabel}
              />
              <DocumentField
                label={t('Payment reference', 'Betalreferens')}
                value={invoice.paymentReference || '—'}
              />
            </Row>
          </>
        ) : null}
      </Column>
    )
  }

  return (
    <Column className="gap-6">
      <Row className="justify-between items-start gap-4">
        <Column className="gap-1">
          <h2>{t('Invoice', 'Faktura')}</h2>
          <span className="text-sm text-muted-foreground">
            {documentNumber}
          </span>
        </Column>
        <Column className="items-end gap-0.5 text-right text-xs text-muted-foreground">
          <span>
            {t('Currency', 'Valuta')}: {invoice.currency}
          </span>
          {!invoice.reverseCharge && (
            <span>
              {t('VAT', 'Moms')}: {invoice.vatRate}%
            </span>
          )}
          {invoice.reverseCharge && (
            <span>{t('Reverse charge', 'Omvänd betalningsskyldighet')}</span>
          )}
        </Column>
      </Row>

      <Row className="justify-between items-start gap-8 flex-wrap">
        <PartyBlock
          title={t('Seller', 'Säljare')}
          party={invoice.seller}
          showFTax={invoice.fTaxApproved}
        />
        <PartyBlock title={t('Buyer', 'Köpare')} party={invoice.buyer} />
      </Row>

      <Row className="flex-wrap gap-x-12 gap-y-4">
        <DocumentField
          label={t('Issue date', 'Fakturadatum')}
          value={formatDocumentDate(invoice.issueDate)}
        />
        <DocumentField
          label={t('Supply date', 'Leveransdatum')}
          value={formatDocumentDate(invoice.supplyDate || invoice.issueDate)}
        />
        <DocumentField
          label={t('Due date', 'Förfallodatum')}
          value={formatDocumentDate(invoice.dueDate)}
        />
        <DocumentField
          label={t('Payment terms', 'Betalningsvillkor')}
          value={paymentTermsLabel}
        />
        <DocumentField
          label={t('Payment reference', 'Betalreferens')}
          value={invoice.paymentReference || '—'}
        />
        {(invoice.ourReference || invoice.yourReference) && (
          <>
            {invoice.ourReference && (
              <DocumentField
                label={t('Our reference', 'Vår referens')}
                value={invoice.ourReference}
              />
            )}
            {invoice.yourReference && (
              <DocumentField
                label={t('Your reference', 'Er referens')}
                value={invoice.yourReference}
              />
            )}
          </>
        )}
      </Row>
    </Column>
  )
}
